import type { CostAccess, CostInvestorCheck, CostApplication, CostApplicationRow, CostApplicationStatus, CostContract, CostContractKind, CostPendingAction } from '../types'
import { fromCents, toCents } from './contracts'
import type { ModuleActions } from './permissions'
import { normalizeSearch, type CostTree, type CostTreeNode } from './tree'

const ZERO = BigInt(0)
const TWO = BigInt(2)
const BASIS = BigInt(10000) // a percent with two places is whole basis points: "12.50" → 1250
/** n / d rounded half up, for n ≥ 0 and d > 0. */
const roundDiv = (n: bigint, d: bigint): bigint => (n * TWO + d) / (d * TWO)
const cents = (v: string | null | undefined): bigint => (v ? toCents(v) : ZERO)

// ---- Rights (1b-A spec §8) ----

export interface ApplicationAffordances {
  editDraft: boolean
  submit: boolean
  remove: boolean
  review: boolean
  returnIt: boolean
  certify: boolean
  uncertify: boolean
  invoice: boolean
  /** Record the invoice issued to the investor (a certified main-contract application). */
  investorInvoice: boolean
  addAttachment: boolean
  removeAttachment: boolean
  protocol: boolean
}

/** The server enforces the same; this only hides what it would refuse. `latest`: the contract's newest application. */
export function applicationAffordances(a: ModuleActions, app: Pick<CostApplication, 'status' | 'invoiceId' | 'investorInvoice'> | null, latest: boolean, access: CostAccess = 'MODULE', kind: CostContractKind = 'SUB'): ApplicationAffordances {
  const is = (s: CostApplicationStatus) => app?.status === s
  if (access === 'REPRESENTATIVE') {
    // 1b-B spec §8: the contractor's representative fills and submits his draft; review and invoicing stay with the investor.
    const draft = is('DRAFT')
    return { editDraft: draft, submit: draft, remove: draft, review: false, returnIt: false, certify: false, uncertify: false, invoice: false, investorInvoice: false, addAttachment: draft, removeAttachment: draft, protocol: is('CERTIFIED') }
  }
  return {
    editDraft: a.canEdit && is('DRAFT'),
    submit: a.canEdit && is('DRAFT'),
    remove: a.canDelete && is('DRAFT'),
    review: a.canApprove && is('SUBMITTED'),
    returnIt: a.canApprove && is('SUBMITTED'),
    certify: a.canApprove && is('SUBMITTED'),
    uncertify: a.canApprove && is('CERTIFIED') && !app?.invoiceId && !app?.investorInvoice && latest,
    invoice: kind === 'SUB' && a.canCreate && is('CERTIFIED') && !app?.invoiceId,
    investorInvoice: kind === 'MAIN' && a.canEdit && is('CERTIFIED'),
    addAttachment: a.canCreate && !!app,
    removeAttachment: a.canDelete && !!app,
    protocol: a.canView && is('CERTIFIED'),
  }
}

/** "Nový soupis" is shown on works contracts to those who may create, and to the contractor's representative (1b-B spec §8). */
export const canCreateApplication = (a: ModuleActions, c: Pick<CostContract, 'kind'>, access: CostAccess = 'MODULE'): boolean =>
  c.kind === 'SUB' ? a.canCreate || access === 'REPRESENTATIVE' : a.canCreate

/** Why "Nový soupis" is off (an i18n key), or null (spec §2). */
export function newApplicationBlocker(c: Pick<CostContract, 'status'>, applications: Pick<CostApplication, 'status'>[]): string | null {
  if (c.status !== 'ACTIVE') return 'CostControl_application_new_needs_active'
  if (applications.some((x) => x.status !== 'CERTIFIED')) return 'CostControl_application_new_open_exists'
  return null
}

// ---- % ↔ Kč (decision 2) and totals, in whole cents ----

/** The cumulative % done of a row after this period, two places, half up. */
export function percentDone(value: string, previous: string, requested: string): string {
  const v = toCents(value)
  if (v <= ZERO) return '0.00'
  return fromCents(roundDiv((cents(previous) + cents(requested)) * BASIS, v))
}

/** The period amount that brings a row to `pct` % done: round(value × pct / 100) − previous, never below 0. */
export function requestedFromPercent(value: string, previous: string, pct: string): string {
  const r = roundDiv(toCents(value) * toCents(pct), BASIS) - cents(previous)
  return fromCents(r > ZERO ? r : ZERO)
}

/** The server's retentionOf: half up to cents of amount × percent / 100. */
export const retentionCents = (amount: string, percent: string | null): string => {
  if (!percent) return '0.00'
  const n = toCents(amount) * toCents(percent)
  // A deduction (negative base) rounds half away from zero, as the server does.
  return fromCents(n < ZERO ? -roundDiv(-n, BASIS) : roundDiv(n, BASIS))
}

export interface ApplicationTotals { requested: string; certified: string; retention: string; payable: string }

/** Requested and certified summed in cents; retention on the certified total, as on the invoice that follows (spec §4–5). */
export function applicationTotals(rows: { requested: string; certified: string }[], retentionPercent: string | null): ApplicationTotals {
  const requested = rows.reduce((acc, r) => acc + cents(r.requested), ZERO)
  const certified = rows.reduce((acc, r) => acc + cents(r.certified), ZERO)
  const retention = toCents(retentionCents(fromCents(certified), retentionPercent))
  return { requested: fromCents(requested), certified: fromCents(certified), retention: fromCents(retention), payable: fromCents(certified - retention) }
}

/** True when everything certified before plus this application's amount (`thisAmount`) exceeds the contract's current value (spec §16 — change lines may be negative). */
export const exceedsContractValue = (rows: { previous: string }[], thisAmount: string, contractValue: string): boolean =>
  rows.reduce((acc, r) => acc + cents(r.previous), cents(thisAmount)) > toCents(contractValue)

// ---- Draft (DRAFT, EDIT) ----

/** An edited period amount: the parsed value (null = empty) and whether the typed text is valid. */
export interface DraftAmount { value: string | null; valid: boolean }
export type DraftAmounts = Record<string, DraftAmount>

/** The period amount of a row in the editor: the edit, else what is stored. */
export const draftRequested = (r: CostApplicationRow, draft: DraftAmounts): string | null => (r.key in draft ? draft[r.key].value : r.requested)

// ---- Manual rows (Brema spec §3.4): penalties and deductions, signed, with an optional budget item ----

/** A manual row in the editor. `amount` is the requested sum in a draft and the certified sum in a review; `contractor` rows keep their text and requested sum. */
export interface ManualRow { id: string; key: string | null; description: string; nodeId: string | null; amount: string | null; valid: boolean; note: string; contractor: boolean; requested: string
  /** The paired item's name from the API, for a viewer without the budget tree. */
  label: string | null }

export const newManualRow = (id: string): ManualRow => ({ id, key: null, description: '', nodeId: null, amount: null, valid: true, note: '', contractor: false, requested: '0.00', label: null })

/** The editor's rows from the stored ones: a draft edits the contractor's rows, a review sees all of them. */
export function manualEditorRows(stored: CostApplicationRow[], review: boolean): ManualRow[] {
  return stored
    .filter((r) => review || cents(r.requested) !== ZERO)
    .map((r) => ({
      id: r.key, key: r.key, description: r.description ?? '', nodeId: r.nodeId,
      amount: review ? r.certified ?? r.requested : r.requested, valid: true, note: r.note ?? '', contractor: cents(r.requested) !== ZERO, requested: r.requested,
      label: r.nodeId ? (r.code ? `${r.code} · ${r.name}` : r.name) || null : null,
    }))
}

/**
 * The first manual-row problem as an i18n key, or null (the server's checkManualLines / checkManualReview). A draft edits
 * every row; a review locks the contractor's text and reviews his amount as any row's: not above the request, a positive
 * one not below 0, a lowered one with a reason.
 */
export function manualProblem(manual: ManualRow[], review: boolean): string | null {
  const locked = manual.filter((m) => review && m.contractor)
  const open = manual.filter((m) => !(review && m.contractor))
  if (manual.some((m) => !m.valid) || locked.some((m) => m.amount === null)) return 'CostControl_application_problem_invalid'
  if (open.some((m) => !m.description.trim() || cents(m.amount) === ZERO)) return 'CostControl_application_manual_problem'
  if (locked.some((m) => cents(m.amount) > cents(m.requested))) return 'CostControl_error_application_certified_above_requested'
  if (locked.some((m) => cents(m.requested) > ZERO && cents(m.amount) < ZERO)) return 'CostControl_error_negative_amount'
  if (locked.some((m) => cents(m.amount) < cents(m.requested) && !m.note.trim())) return 'CostControl_error_application_note_required'
  return null
}

/** What "Uložit" sends as `manualLines`: negative rows stay, descriptions are trimmed. */
export const toManualInputs = (manual: ManualRow[]): { rowKey: string | null; description: string; nodeId: string | null; requested: string }[] =>
  manual.map((m) => ({ rowKey: m.key, description: m.description.trim(), nodeId: m.nodeId, requested: m.amount ?? '0.00' }))

/** What the review sends as `manualRows`: every contractor row plus the reviewer's own. */
export const toManualReviewInputs = (manual: ManualRow[]): { rowKey: string | null; description: string; nodeId: string | null; certified: string; note: string | null }[] =>
  manual.map((m) => ({ rowKey: m.key, description: m.description.trim(), nodeId: m.nodeId, certified: m.amount ?? '0.00', note: m.note.trim() || null }))

/** The first problem as an i18n key (the server checks the same), or null. */
export function draftProblem(rows: CostApplicationRow[], draft: DraftAmounts, manual: ManualRow[] | null = null): string | null {
  if (manual) { const p = manualProblem(manual, false); if (p) return p }
  if (rows.some((r) => r.key in draft && !draft[r.key].valid)) return 'CostControl_application_problem_invalid'
  if (rows.some((r) => cents(draftRequested(r, draft)) > ZERO && cents(r.previous) + cents(draftRequested(r, draft)) > toCents(r.value))) return 'CostControl_error_application_over_value'
  return null
}

/** What "Uložit" sends: every row with an amount, in row order. */
export const toLineInputs = (rows: CostApplicationRow[], draft: DraftAmounts): { key: string; requested: string }[] =>
  rows.map((r) => ({ key: r.key, requested: draftRequested(r, draft) ?? '0.00' })).filter((l) => cents(l.requested) > ZERO)

export const isDraftDirty = (rows: CostApplicationRow[], draft: DraftAmounts): boolean =>
  rows.some((r) => r.key in draft && cents(draft[r.key].value) !== cents(r.requested))

// ---- Review (SUBMITTED, APPROVE) ----

export interface DraftReview { certified: string | null; valid: boolean; note: string }
export type DraftReviews = Record<string, DraftReview>

/** A row's review in the editor: the edit, else the stored review, else certified as requested. */
export const reviewOf = (r: CostApplicationRow, review: DraftReviews): DraftReview =>
  review[r.key] ?? { certified: r.certified ?? r.requested, valid: true, note: r.note ?? '' }

export type RowsMode = 'draft' | 'review' | 'view'
export interface RowFigures { certified: string | null; counted: string; lowered: boolean; remaining: string; reviewable: boolean }

/**
 * One row's figures on screen, for both rows tables. `requested`: the period amount shown (draftRequested), `rv`: the
 * row's review (reviewOf). `certified` is null while nothing is certified; `counted` is what the row bills — the request
 * in a draft, else the certified amount — and so the basis of its cumulative % and of what remains.
 */
export function rowFigures(r: CostApplicationRow, requested: string, rv: DraftReview, mode: RowsMode, status: CostApplicationStatus): RowFigures {
  const certified = mode === 'review' ? rv.certified ?? '0.00' : r.certified ?? (status === 'DRAFT' ? null : requested)
  const counted = status === 'DRAFT' ? requested : certified ?? requested
  const asked = cents(requested) > ZERO
  return {
    certified,
    counted,
    lowered: certified !== null && asked && cents(certified) < cents(requested),
    remaining: fromCents(cents(r.value) - cents(r.previous) - cents(counted)),
    reviewable: mode === 'review' && asked,
  }
}

const billed = (rows: CostApplicationRow[]) => rows.filter((r) => cents(r.requested) > ZERO)

/** The first problem as an i18n key (the server's checkReview), or null. */
export function reviewProblem(rows: CostApplicationRow[], review: DraftReviews, manual: ManualRow[] | null = null): string | null {
  if (manual) { const p = manualProblem(manual, true); if (p) return p }
  const reviews = billed(rows).map((r) => ({ r, rv: reviewOf(r, review) }))
  if (reviews.some(({ rv }) => !rv.valid || rv.certified === null)) return 'CostControl_application_problem_invalid'
  if (reviews.some(({ r, rv }) => cents(rv.certified) > toCents(r.requested))) return 'CostControl_error_application_certified_above_requested'
  if (reviews.some(({ r, rv }) => cents(rv.certified) < toCents(r.requested) && !rv.note.trim())) return 'CostControl_error_application_note_required'
  return null
}

/** What "Uložit kontrolu" and "Odsouhlasit" send: only rows adjusted or with a reason; the rest are certified as requested. */
export const toReviewInputs = (rows: CostApplicationRow[], review: DraftReviews): { key: string; certified: string; note: string | null }[] =>
  billed(rows)
    .map((r) => ({ r, rv: reviewOf(r, review) }))
    .filter(({ r, rv }) => cents(rv.certified) !== toCents(r.requested) || !!rv.note.trim())
    .map(({ r, rv }) => ({ key: r.key, certified: rv.certified as string, note: rv.note.trim() || null }))

export const isReviewDirty = (rows: CostApplicationRow[], review: DraftReviews): boolean =>
  rows.some((r) => r.key in review && (cents(review[r.key].certified) !== cents(r.certified ?? r.requested) || review[r.key].note.trim() !== (r.note ?? '')))

// ---- Subcontractor billing ahead of the investor's (investor billing spec §7) ----

export interface InvestorWarning { investorBilled: string; subcontractor: string }

/** The nodes where other applications + this one (rows via `amountOf`, paired manual rows) bill more than the investor was. Warns only; never blocks. */
export function investorWarnings(rows: CostApplicationRow[], amountOf: (r: CostApplicationRow) => string, manual: ManualRow[], check: CostInvestorCheck[] | null): Map<string, InvestorWarning> {
  const out = new Map<string, InvestorWarning>()
  if (!check) return out
  const here = new Map<string, bigint>()
  const add = (nodeId: string | null, amount: string | null) => { if (nodeId) here.set(nodeId, (here.get(nodeId) ?? ZERO) + cents(amount)) }
  rows.forEach((r) => add(r.nodeId, amountOf(r)))
  manual.forEach((m) => add(m.nodeId, m.amount))
  check.forEach((c) => {
    const total = cents(c.subcontractorBilledOther) + (here.get(c.nodeId) ?? ZERO)
    if (total > cents(c.investorBilled)) out.set(c.nodeId, { investorBilled: c.investorBilled, subcontractor: fromCents(total) })
  })
  return out
}

// ---- Filter and sections ----

export interface RowFilter { onlyBilled: boolean; search: string }

/** `requestedOf` gives the amount on screen (the draft's in the editor). */
export function visibleRows(rows: CostApplicationRow[], f: RowFilter, requestedOf: (r: CostApplicationRow) => string | null): CostApplicationRow[] {
  const q = normalizeSearch(f.search)
  return rows.filter((r) => (!f.onlyBilled || r.kind === 'MANUAL' || cents(requestedOf(r)) > ZERO)
    && (!q || [r.name, r.code, r.section, r.changeTitle, r.nodePath, r.description].some((s) => !!s && normalizeSearch(s).includes(q))))
}

// ---- The investor application by the budget tree (spec §2.3) ----

export type InvestorTableRow =
  /** `current`: Σ amountOf (this period as requested); `counted`: Σ countedOf, the basis of the group's cumulative %. */
  | { kind: 'group'; id: string; depth: number; name: string; code: string | null; value: string; previous: string; current: string; counted: string }
  | { kind: 'row'; row: CostApplicationRow; depth: number }

/**
 * The rows of a main-contract application in tree order: a group (with the value, previous, this-period and counted sums
 * of all its rows, search and filter aside) before what is below it, and only when something is. A search keeps the matching rows, every row
 * of a matching group, and their ancestors, and opens collapsed groups as the budget grid does. Rows the tree does not
 * place (manual rows, unknown items) follow it. `countedOf` is what a row bills (rowFigures' `counted`), so a group's %
 * has its rows' basis.
 */
export function investorTableRows(tree: CostTree, rows: CostApplicationRow[], amountOf: (r: CostApplicationRow) => string, opts: { collapsed: Set<string>; search: string; onlyWithAmount: boolean }, countedOf: (r: CostApplicationRow) => string = amountOf): InvestorTableRow[] {
  const q = normalizeSearch(opts.search)
  const closed = q ? new Set<string>() : opts.collapsed
  const hit = (...texts: (string | null)[]) => texts.some((s) => !!s && normalizeSearch(s).includes(q))
  const keep = (r: CostApplicationRow, matched: boolean) =>
    (!opts.onlyWithAmount || r.kind === 'MANUAL' || cents(amountOf(r)) !== ZERO) && (!q || matched || hit(r.code, r.name, r.description))
  const byNode = new Map<string, CostApplicationRow[]>()
  const loose: CostApplicationRow[] = []
  rows.forEach((r) => {
    if (r.kind === 'MANUAL' || !r.nodeId || !tree.byId.has(r.nodeId)) loose.push(r)
    else byNode.set(r.nodeId, [...(byNode.get(r.nodeId) ?? []), r])
  })
  const out: InvestorTableRow[] = []
  type Sum = { value: bigint; previous: bigint; current: bigint; counted: bigint; shown: number }
  const add = (s: Sum, r: CostApplicationRow) => { s.value += cents(r.value); s.previous += cents(r.previous); s.current += cents(amountOf(r)); s.counted += cents(countedOf(r)) }
  const visit = (n: CostTreeNode, matched: boolean, emit: boolean): Sum => {
    const s: Sum = { value: ZERO, previous: ZERO, current: ZERO, counted: ZERO, shown: 0 }
    const { id, name, code } = n.activity
    if (n.activity.kind !== 'GROUP') {
      for (const r of byNode.get(id) ?? []) {
        add(s, r) // the sums cover every row, whatever the search or filter shows
        if (keep(r, matched)) { s.shown++; if (emit) out.push({ kind: 'row', row: r, depth: n.depth }) }
      }
      return s
    }
    const at = out.length
    if (emit) out.push({ kind: 'group', id, depth: n.depth, name, code, value: '', previous: '', current: '', counted: '' })
    const inside = matched || (!!q && hit(name, code))
    n.children.forEach((c) => {
      const k = visit(c, inside, emit && !closed.has(id))
      s.value += k.value; s.previous += k.previous; s.current += k.current; s.counted += k.counted; s.shown += k.shown
    })
    if (emit && !s.shown) out.length = at
    else if (emit) out[at] = { kind: 'group', id, depth: n.depth, name, code, value: fromCents(s.value), previous: fromCents(s.previous), current: fromCents(s.current), counted: fromCents(s.counted) }
    return s
  }
  tree.roots.forEach((r) => visit(r, false, true))
  loose.filter((r) => keep(r, false)).forEach((r) => out.push({ kind: 'row', row: r, depth: 0 }))
  return out
}

export interface RowSection { key: string; label: string | null; changes: boolean; manual: boolean; rows: CostApplicationRow[] }

/** Consecutive rows of one section form a group; change rows form their own (labelled by the client). */
export function rowSections(rows: CostApplicationRow[]): RowSection[] {
  const out: RowSection[] = []
  let lastSame = ''
  rows.forEach((r) => {
    const changes = r.kind === 'CHANGE_LINE'
    const manual = r.kind === 'MANUAL'
    const same = `${changes}|${manual}|${manual ? '' : r.section ?? ''}`
    const last = out[out.length - 1]
    if (last && lastSame === same) last.rows.push(r)
    else out.push({ key: r.key, label: changes || manual ? null : r.section, changes, manual, rows: [r] })
    lastSame = same
  })
  return out
}

// ---- Header form ----

const pad = (n: number) => String(n).padStart(2, '0')
/** The calendar month before `today` (local), as YYYY-MM-DD. */
export function previousMonth(today: Date = new Date()): { from: string; to: string } {
  const year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear()
  const month = today.getMonth() === 0 ? 12 : today.getMonth() // 1-based
  const last = new Date(year, month, 0).getDate()
  return { from: `${year}-${pad(month)}-01`, to: `${year}-${pad(month)}-${pad(last)}` }
}

export interface ApplicationForm { periodFrom: string | null; periodTo: string | null; note: string }
export const emptyApplicationForm = (today: Date = new Date()): ApplicationForm => {
  const p = previousMonth(today)
  return { periodFrom: p.from, periodTo: p.to, note: '' }
}
export const formFromApplication = (a: Pick<CostApplication, 'periodFrom' | 'periodTo' | 'note'>): ApplicationForm =>
  ({ periodFrom: a.periodFrom, periodTo: a.periodTo, note: a.note ?? '' })
export function applicationFormProblem(f: ApplicationForm): string | null {
  if (!f.periodFrom || !f.periodTo) return 'CostControl_application_period_required'
  if (f.periodTo < f.periodFrom) return 'CostControl_error_application_period_order'
  return null
}
export const applicationInput = (f: ApplicationForm): Record<string, unknown> => ({ periodFrom: f.periodFrom, periodTo: f.periodTo, note: f.note.trim() || null })

// ---- "Ke schválení" (spec §9) ----

const addDays = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}
export type DueState = 'overdue' | 'soon' | null
/** An invoice past its due date, or due within 7 days of `today` (YYYY-MM-DD). */
export function dueState(dueDate: string | null, today: string): DueState {
  if (!dueDate) return null
  if (dueDate < today) return 'overdue'
  return dueDate <= addDays(today, 7) ? 'soon' : null
}

export function pendingPath(projectId: string, a: Pick<CostPendingAction, 'type' | 'id'>): string {
  const base = `/project/${projectId}/cost-control`
  if (a.type === 'APPLICATION') return `${base}/applications/${a.id}`
  if (a.type === 'CHANGE') return `${base}/changes/${a.id}`
  return `${base}/invoices/${a.id}`
}

// ---- Retention (spec §5) ----

export interface ReleaseForm { date: string | null; amount: string | null; amountValid: boolean; note: string }
export const emptyReleaseForm = (today: string): ReleaseForm => ({ date: today, amount: null, amountValid: true, note: '' })
export function releaseFormProblem(f: ReleaseForm, remaining: string): string | null {
  if (!f.date) return 'CostControl_retention_date_required'
  if (!f.amountValid) return 'CostControl_error_bad_amount'
  if (cents(f.amount) <= ZERO) return 'CostControl_error_amount_positive'
  if (cents(f.amount) > toCents(remaining)) return 'CostControl_error_retention_release_too_large'
  return null
}

/** A "Pozastávka %" field: empty is fine, otherwise 0–100. */
export function percentFieldProblem(value: string | null, valid: boolean): string | null {
  if (!valid) return 'CostControl_error_bad_amount'
  if (value && toCents(value) > toCents('100.00')) return 'CostControl_error_retention_percent_range'
  return null
}
